import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useWallet } from "@solana/wallet-adapter-react";
import { VersionedTransaction } from "@solana/web3.js";
import { nftFetch, useRippWallet } from "./wallet";
import { RippLayout } from "./ui";
import { NftShowcase } from "../components/NftShowcase";

const b64ToBytes = (b64) => Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
const bytesToB64 = (bytes) => btoa(String.fromCharCode(...bytes));
const PHASE_LABEL = { not_deployed: "NOT DEPLOYED", upcoming: "OPENS SOON", allowlist: "ALLOWLIST PHASE", public: "PUBLIC MINT LIVE", ended: "ENDED", paused: "DATE TBA" };

export function useCountdown(targetTs, serverOffset = 0) {
  const [left, setLeft] = useState(null);
  useEffect(() => {
    if (!targetTs) return setLeft(null);
    const tick = () => setLeft(Math.max(0, targetTs - (Date.now() / 1000 + serverOffset)));
    tick(); const id = setInterval(tick, 1000); return () => clearInterval(id);
  }, [targetTs, serverOffset]);
  return left;
}

export const fmtLeft = (s) => {
  if (s == null) return "—";
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60), sec = Math.floor(s % 60);
  return d > 0 ? `${d}d ${h}h ${m}m ${sec}s` : `${h}h ${m}m ${sec}s`;
};

export default function RippMint() {
  const w = useRippWallet();
  const { signTransaction } = useWallet();
  const [d, setD] = useState(null); const [err, setErr] = useState(null); const [busy, setBusy] = useState(false); const [step, setStep] = useState(null); const [done, setDone] = useState(null);
  const load = useCallback(async () => { try { setD(await nftFetch(`/api/nft/drop${w.wallet ? `?wallet=${w.wallet}` : ""}`)); } catch (e) { setErr(e.message); } }, [w.wallet]);
  useEffect(() => { load(); const id = setInterval(load, 30000); return () => clearInterval(id); }, [load]);
  const offset = d ? d.server_time - Date.now() / 1000 : 0;
  const nextTs = d && (d.phase === "upcoming" ? (d.allowlist_start_ts || d.start_ts) : d.phase === "allowlist" ? d.start_ts : null);
  const left = useCountdown(nextTs, offset);
  useEffect(() => { if (left === 0) load(); }, [left, load]);

  const mint = async () => {
    setBusy(true); setErr(null); setDone(null);
    try {
      setStep("Building transaction…");
      const built = await nftFetch("/api/nft/drop/mint-tx", { method: "POST", body: JSON.stringify({ wallet: w.wallet }) });
      setStep(`Approve in your wallet — ${built.price_sol} SOL + network fee`);
      const tx = VersionedTransaction.deserialize(b64ToBytes(built.tx_base64));
      const signed = await signTransaction(tx);
      setStep("Sending to Solana… waiting for confirmation");
      const res = await nftFetch("/api/nft/drop/submit", { method: "POST", body: JSON.stringify({ asset_address: built.asset_address, signed_tx_base64: bytesToB64(signed.serialize()) }) });
      setDone(res); await load();
    } catch (e) { setErr(e.message); }
    setStep(null); setBusy(false);
  };

  if (!d) return <RippLayout><div className="text-white/40 text-xs tracking-widest" data-testid="mint-loading">{err || "LOADING…"}</div></RippLayout>;
  const live = d.phase === "public" || d.phase === "allowlist";
  return (
    <RippLayout>
      <div className="grid gap-10 lg:grid-cols-[minmax(0,42rem)_minmax(0,1fr)] items-start">
      <div className="max-w-2xl" data-testid="mint-page">
        <div className="text-[11px] tracking-[0.4em] text-[#F5C451]/80">MINT · {d.network.toUpperCase()}</div>
        <h1 className="mt-4 text-5xl font-black">RIPP public mint</h1>
        <div className="mt-8 rounded-2xl border border-white/10 bg-[#0b0b0b] p-6 space-y-4" data-testid="mint-status">
          <div className="flex items-center justify-between">
            <span className={`text-[11px] tracking-[0.3em] px-3 py-1 rounded-full border ${live ? "border-emerald-400/50 text-emerald-300" : "border-white/15 text-white/60"}`} data-testid="mint-phase">{PHASE_LABEL[d.phase] || d.phase}</span>
            {nextTs && <span className="font-mono text-2xl text-[#F5C451]" data-testid="mint-countdown">{fmtLeft(left)}</span>}
          </div>
          {d.phase === "upcoming" && <div className="text-xs text-white/50" data-testid="mint-next-label">{d.allowlist_start_ts ? "Allowlist opens" : "Public mint opens"} {new Date((d.allowlist_start_ts || d.start_ts) * 1000).toLocaleString()} · public {new Date(d.start_ts * 1000).toLocaleString()}</div>}
          {d.phase === "allowlist" && <div className="text-xs text-white/50">Public mint opens {new Date(d.start_ts * 1000).toLocaleString()}</div>}
          <div className="grid grid-cols-3 gap-3 text-sm pt-2">
            <div className="rounded-xl bg-black/60 p-3"><div className="text-[10px] tracking-widest text-white/40">PRICE</div><div className="text-xl font-bold" data-testid="mint-price">{d.price_sol} SOL</div></div>
            <div className="rounded-xl bg-black/60 p-3"><div className="text-[10px] tracking-widest text-white/40">PER WALLET</div><div className="text-xl font-bold" data-testid="mint-limit">{d.mint_limit}</div></div>
            <div className="rounded-xl bg-black/60 p-3"><div className="text-[10px] tracking-widest text-white/40">MINTED</div><div className="text-xl font-bold" data-testid="mint-supply">{d.items_redeemed ?? "—"} / {d.items_loaded ?? "—"}</div></div>
          </div>
          <div className="text-xs text-white/50 flex flex-wrap gap-x-4 gap-y-1 pt-2">
            <span>Candy machine <span className="font-mono">{d.candy_machine ? `${d.candy_machine.slice(0, 6)}…${d.candy_machine.slice(-4)}` : "—"}</span></span>
            {d.allowlist_size > 0 && <span>Allowlist {d.allowlist_size} wallets · opens 24h early</span>}
            {w.wallet && <span data-testid="mint-wallet-info">You: {d.wallet_minted}/{d.mint_limit} minted{d.wallet_allowlisted ? " · allowlisted" : ""}</span>}
          </div>
        </div>
        <div className="mt-6 flex items-center gap-4 flex-wrap">
          {!w.wallet && <button onClick={w.openWallets} className="px-6 py-3 rounded-full bg-[#F5C451] text-black text-xs font-bold tracking-widest" data-testid="mint-connect-btn">CONNECT WALLET</button>}
          {w.wallet && <button onClick={mint} disabled={busy || !d.can_mint} className="px-6 py-3 rounded-full bg-[#F5C451] text-black text-xs font-bold tracking-widest disabled:opacity-40" data-testid="mint-btn">{busy ? "MINTING…" : `MINT 1 · ${d.price_sol} SOL`}</button>}
          {w.wallet && !d.can_mint && <span className="text-xs text-white/50" data-testid="mint-reason">{d.reason}</span>}
          {w.session && <Link to="/ripp/my-ripps" className="text-xs text-[#F5C451] underline" data-testid="mint-goto-myripps">view my RIPPs</Link>}
        </div>
        {step && <div className="mt-4 text-xs text-white/70 font-mono" data-testid="mint-step">{step}</div>}
        {err && <div className="mt-4 text-xs text-red-300 font-mono break-all" data-testid="mint-error">{err}</div>}
        {done && <div className="mt-4 rounded-xl border border-emerald-400/30 p-4 text-sm" data-testid="mint-success">Minted {done.item ? done.item.name : done.asset?.name} → <span className="font-mono text-xs">{done.asset?.owner}</span>
          <div className="text-xs mt-1"><a className="text-[#F5C451] underline" href={`https://solscan.io/tx/${done.signature}${d.network === "devnet" ? "?cluster=devnet" : ""}`} target="_blank" rel="noreferrer">view transaction</a></div></div>}
        <div className="mt-8 text-xs text-white/40">Wallet Standard (Phantom · Solflare · Backpack). The server builds the mint transaction; only your wallet signs it. We never ask for a seed phrase or private key.</div>
      </div>
      <div className="lg:sticky lg:top-24"><NftShowcase testid="mint-nft-showcase" /></div>
      </div>
    </RippLayout>
  );
}
